import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { aiRequest, aiStream, newRequestId } from "../../apis/ai";
import { useListPageCache } from "../../context/ListPageCacheContext";
import { useVoiceCall } from "./useVoiceCall";

const AssistantContext = createContext(null);

export function AssistantProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [status, setStatus] = useState(null);
  const [threads, setThreads] = useState([]);
  const [threadId, setThreadId] = useState(null);
  const [turns, setTurns] = useState([]);
  const [live, setLive] = useState(null);
  const [draft, setDraft] = useState("");
  const [mode, setMode] = useState("ask");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [uploads, setUploads] = useState([]);
  const [uploading, setUploading] = useState(false);
  const active = useRef(false);
  const controller = useRef(null);
  const generation = useRef(0);
  const mounted = useRef(true);
  const location = useLocation();
  const navigate = useNavigate();
  const lastERPPage = useRef({ pathname: "/", search: "", hash: "", state: null });
  const { clearAllCache } = useListPageCache();
  const voice = useVoiceCall({ threadId, mode, onThread: setThreadId, onTurns: setTurns, onError: setError });

  useEffect(() => {
    if (location.pathname !== "/assistant") {
      lastERPPage.current = location;
    } else {
      setMinimized(false);
    }
  }, [location]);

  const minimize = () => {
    setOpen(false);
    setMinimized(true);
    if (location.pathname === "/assistant") {
      const { pathname, search, hash, state } = lastERPPage.current;
      navigate({ pathname, search, hash }, { state, replace: true });
    }
  };

  const restore = () => {
    setMinimized(false);
    setOpen(true);
  };

  const refreshThreads = async () => {
    const result = await aiRequest("/threads");
    if (mounted.current) setThreads(result.items || []);
  };

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      generation.current += 1;
      controller.current?.abort();
    };
  }, []);

  useEffect(() => {
    if (!open && location.pathname !== "/assistant") return;
    let cancelled = false;
    Promise.all([aiRequest("/status"), aiRequest("/threads")])
      .then(([settings, history]) => {
        if (!cancelled) {
          setStatus(settings);
          setThreads(history.items || []);
        }
      })
      .catch((caught) => { if (!cancelled) setError(caught.message); });
    return () => { cancelled = true; };
  }, [open, location.pathname]);

  const loadThread = async (id) => {
    if (active.current || voice.active) return;
    const current = ++generation.current;
    active.current = true;
    setLoading(true);
    setError("");
    setTurns([]);
    setLive(null);
    setThreadId(id);
    setDraft("");
    try {
      const result = await aiRequest(`/threads/${encodeURIComponent(id)}`);
      if (mounted.current && current === generation.current) {
        setTurns(result.turns || []);
        setMode(result.turns?.at(-1)?.mode || "ask");
      }
    } catch (caught) {
      if (mounted.current && current === generation.current) setError(caught.message);
    } finally {
      if (mounted.current && current === generation.current) {
        active.current = false;
        setLoading(false);
      }
    }
  };

  const newThread = () => {
    if (active.current || voice.active) return;
    generation.current += 1;
    setThreadId(null);
    setTurns([]);
    setLive(null);
    setDraft("");
    setError("");
    setUploads([]);
  };

  const send = async (message = draft) => {
    message = message.trim();
    if ((!message && !uploads.length) || active.current || voice.active || uploading || !status?.enabled) return;
    active.current = true;
    setBusy(true);
    setError("");
    setDraft("");
    const requestId = newRequestId();
    const pending = { id: requestId, message: message || "Review the attached files", attachments: uploads, content: "", tools: [], results: [], reports: [], confirmations: [], status: "running" };
    setLive(pending);
    controller.current = new AbortController();
    let accepted = false;
    try {
      const result = await aiStream({ request_id: requestId, thread_id: threadId || undefined, mode, message, attachment_ids: uploads.map((file) => file.id) }, (type, data) => {
        if (!mounted.current) return;
        if (type === "started") {
          accepted = true;
          setThreadId(data.thread.id);
          setUploads([]);
        } else if (type === "delta") {
          setLive((current) => current && ({ ...current, content: current.content + data.text }));
        } else if (type === "tool") {
          setLive((current) => current && ({ ...current, tools: [...current.tools.filter((tool) => tool.id !== data.id), data] }));
        } else if (type === "result") {
          setLive((current) => current && ({ ...current, results: [...current.results, data] }));
        }
      }, controller.current.signal);
      if (mounted.current) {
        setTurns((current) => [...current.filter((turn) => turn.id !== result.id), result]);
        setThreadId(result.thread_id);
        setLive(null);
        await refreshThreads();
      }
    } catch (caught) {
      if (mounted.current) {
        setError(caught.name === "AbortError" ? "Response stopped. Reload the conversation for its saved status." : caught.message);
        if (!accepted) setDraft(message);
        setLive((current) => current && ({ ...current, status: "interrupted" }));
      }
    } finally {
      if (mounted.current) {
        active.current = false;
        setBusy(false);
      }
      controller.current = null;
    }
  };

  const decide = async (confirmation, approve) => {
    if (active.current) return;
    active.current = true;
    setBusy(true);
    setError("");
    try {
      const result = await aiRequest(`/confirmations/${encodeURIComponent(confirmation.id)}`, { method: "POST", body: JSON.stringify({ approve }) });
      setTurns((current) => current.map((turn) => ({ ...turn, confirmations: turn.confirmations.map((item) => item.id === result.id ? result : item) })));
      if (result.status === "applied") clearAllCache();
    } catch (caught) {
      setError(caught.message);
    } finally {
      active.current = false;
      setBusy(false);
    }
  };

  const removeThread = async (id) => {
    if (active.current || voice.active) return;
    try {
      await aiRequest(`/threads/${encodeURIComponent(id)}`, { method: "DELETE" });
      if (id === threadId) newThread();
      await refreshThreads();
    } catch (caught) {
      setError(caught.message);
    }
  };

  const uploadFiles = async (files) => {
    if (active.current || voice.active || uploading) return;
    if (files.length + uploads.length > 10) { setError("At most 10 attachments are allowed per message."); return; }
    setUploading(true);
    setError("");
    try {
      let total = uploads.reduce((sum, file) => sum + file.size, 0);
      for (const file of Array.from(files)) {
        total += file.size;
        if (file.size > 12 * 1024 * 1024 || total > 50 * 1024 * 1024) throw new Error("Attachments exceed the 12 MiB file or 50 MiB message limit.");
        const body = new FormData();
        body.append("file", file);
        const uploaded = await aiRequest("/files", { method: "POST", body });
        if (mounted.current) setUploads((current) => [...current, uploaded]);
      }
    } catch (caught) { setError(caught.message); }
    finally { if (mounted.current) setUploading(false); }
  };

  const removeUpload = async (file) => {
    try {
      await aiRequest(`/files/${encodeURIComponent(file.id)}`, { method: "DELETE" });
      setUploads((current) => current.filter((item) => item.id !== file.id));
    } catch (caught) { setError(caught.message); }
  };

  return (
    <AssistantContext.Provider value={{ open, setOpen, minimized, minimize, restore, status, threads, threadId, turns, live, draft, setDraft, mode, setMode, busy, loading, error, setError, send, decide, newThread, loadThread, removeThread, uploads, uploading, uploadFiles, removeUpload, voice, stop: () => controller.current?.abort() }}>
      {children}
    </AssistantContext.Provider>
  );
}

export const useAssistant = () => useContext(AssistantContext);